import { range, isEmpty } from 'lodash/fp';
import { DateTime, Interval } from 'luxon';
import { DateBin, TrendDirection } from 'app/lib/constants';
import { generateTimeSeriesFromRange } from 'app/lib/utils';
import { JobOperationType } from 'app/lib/constants/jobs';

const getCurrentMonth = () => DateTime.utc().startOf('month');

export const getTwelveMonths = () => range(0, 12).map((i) => getCurrentMonth().minus({ month: i }));

export const calcBinFromDateRange = (dateFilter) => {
    const start = dateFilter?.rangeFrom
        ? DateTime.fromISO(dateFilter.rangeFrom).toUTC()
        : DateTime.utc().minus({ year: 1 }).plus({ day: 1 });
    const end = dateFilter?.rangeTo ? DateTime.fromISO(dateFilter.rangeTo).toUTC() : DateTime.utc();

    const days = Interval.fromDateTimes(start, end).count('day') + 1;

    if (days > 100) {
        return DateBin.WEEK;
    }

    if (days > 5) {
        return DateBin.DAY;
    }

    return DateBin.HOUR;
};

function calcTrendDirection(diff) {
    return diff > 0 ? TrendDirection.UP : diff < 0 ? TrendDirection.DOWN : TrendDirection.NEUTRAL;
}

function calcPercentageTrendDiff(diff, initialValue) {
    if (initialValue === 0) {
        return null;
    }

    if (diff === 0) {
        return '0';
    }

    const diffPercentage = (diff / initialValue) * 100;
    const fixedDiffPercentage = diffPercentage.toFixed(2);

    if (diffPercentage > 0) {
        return `+${fixedDiffPercentage}`;
    }

    return fixedDiffPercentage;
}

function formatTrendDiff(diff) {
    if (diff === 0) {
        return '0';
    }

    const fixedDiff = diff.toFixed(2);

    if (Number(fixedDiff) === 0) {
        return '0';
    }

    if (diff > 0) {
        return `+${fixedDiff}`;
    }

    return fixedDiff;
}

export function calcTrend(initialValue, finalValue) {
    if (initialValue === 0 && finalValue === 0) {
        return {
            direction: TrendDirection.NOT_APPLICABLE,
            diff: '0',
            diffPercentage: null
        };
    }

    const diff = finalValue - initialValue;

    return {
        direction: calcTrendDirection(diff),
        diff: formatTrendDiff(diff),
        diffPercentage: calcPercentageTrendDiff(diff, initialValue)
    };
}

export function getLastTwoFullMonths(warehouseRecordRows, warehouseName) {
    const rows = warehouseRecordRows[warehouseName];
    const previousFullMonth = getCurrentMonth().minus({ month: 1 });
    const secondPreviousFullMonth = previousFullMonth.minus({ month: 1 });

    const previousFullMonthSQLDate = previousFullMonth.toSQLDate();
    const secondPreviousFullMonthSQLData = secondPreviousFullMonth.toSQLDate();

    const previousFullMonthRow = rows.find((row) => row[1] === previousFullMonthSQLDate);
    const secondPreviousFullMonthRow = rows.find(
        (row) => row[1] === secondPreviousFullMonthSQLData
    );

    return {
        firstMonth: secondPreviousFullMonthRow?.[2] ?? 0,
        secondMonth: previousFullMonthRow?.[2] ?? 0
    };
}

export const generateDimensions = (data, calculatedDateBin) => {
    const dimensions = [
        {
            name: data.columnDescriptors[0].name,
            type: data.columnDescriptors[0].typeFamily,
            index: 0,
            bin: calculatedDateBin
        }
    ];

    if (isEmpty(data?.columns)) {
        return dimensions;
    }

    if (data.columnDescriptors.length === 3) {
        dimensions.push({
            name: data.columnDescriptors[1].name,
            type: data.columnDescriptors[1].typeFamily,
            index: 1
        });
    }

    return dimensions;
};

export const generateMeasure = (data) => [
    {
        name: data.columnDescriptors[1].name,
        type: data.columnDescriptors[1].typeFamily,
        index: data.columnDescriptors.length - 1,
        aggregation: 'sum'
    }
];

export function generateEmptyColumns({ dimensions, dateFilter, warehouses, users }) {
    const dimensionName = dimensions[0].name;

    switch (dimensionName) {
        case 'TIMESTAMP': {
            return generateTimeSeriesFromRange(dateFilter);
        }
        case 'WAREHOUSE_NAME':
            return warehouses.map((warehouse) => [warehouse]);
        case 'QUERY_USER_NAME':
            return users.map((user) => [user]);
        default:
            return [];
    }
}

export const UsageJobKey = {
    TCO_JOB: 'tcoJob',
    WAAREHOUSE_JOB: 'warehouseJob',
    SNOWFLAKE_HISTORY_INDEXING_JOB: 'snowflakeHistoryIndexingJob'
};

export const UsageJobOperationType = {
    [UsageJobKey.TCO_JOB]: JobOperationType.TABLE_METRICS,
    [UsageJobKey.WAAREHOUSE_JOB]: JobOperationType.WAREHOUSE_USAGE,
    [UsageJobKey.SNOWFLAKE_HISTORY_INDEXING_JOB]: JobOperationType.SNOWFLAKE_HISTORY_INDEXING
};
